import {
  CreateRelocationInput,
  Currency,
  DepositRefundedAt,
  HireUnitType,
  HoldingDepositRefundedBy,
  Measurement,
  RelocationType,
} from "@/gql/graphql";
import dayjs from "dayjs";
import { SupplierRecord } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { GapRentalDistanceMode } from "@/app/GapRentals/Utils/gapRentalDistance";

/**
 * Gap rentals share the Relocation table but are their own product: the form
 * asks for a distance mode and a single number, and `toGapRentalInput` turns
 * those into the relocation's distance fields on submit.
 */
export type GapRentalFormValues = Omit<
  CreateRelocationInput,
  "deliveryOffice" | "count"
> & {
  deliveryOfficeIds: string[];
  quantity: number;
  distance_mode: GapRentalDistanceMode;
  distance_value: number | null;
};

/**
 * Gap rentals are same-office rentals priced off a per-day retail rate. The
 * backend derives hire_unit_rate / hire_units_allowed / extra-day rates from
 * retail_rate (see RelocationService::normaliseGapRental), so those are seeded
 * to zero here and the form only collects the gap-rental-relevant fields.
 */
export function getDefaultGapRentalValues({
  supplier,
}: {
  supplier?: SupplierRecord;
}): GapRentalFormValues {
  return {
    deliveryOfficeIds: [],
    quantity: 1,
    type: RelocationType.GapRental,
    retail_rate: null,
    discount_percent: null,
    supplier: {
      connect: supplier?.id ?? null!,
    },
    supplierInsuranceOption: {
      connect: null!,
    },
    vehicle: {
      connect: null!,
    },
    departureOffice: {
      connect: null!,
    },

    available_from_date: dayjs().format("YYYY-MM-DD"),
    available_to_date: null!,

    booking_fee_amount: 0,
    minimum_hire_units: 5,
    hire_unit_rate: 0,
    hire_unit_type: supplier?.hire_unit_type ?? HireUnitType.Day,
    hire_units_allowed: 0,

    // Gap rentals start unlimited. The distance fields below are written from
    // the mode and value on submit (see toGapRentalInput).
    distance_mode: "UNLIMITED",
    distance_value: null,
    distance_allowed: null,
    extra_distance_allowed_per_extra_hire_unit: null,
    excess_distance_rate: supplier?.excess_distance_rate ?? null,
    extra_hire_unit_rate: 0,

    extra_hire_units_allowed: 0,
    extra_hire_unit_deposit_rate: 0,

    holding_deposit_amount: 0,
    holding_deposit_refunded_by:
      supplier?.holding_deposit_refunded_by ??
      HoldingDepositRefundedBy.Supplier,
    holding_deposit_refunded_at:
      supplier?.holding_deposit_refunded_at ?? DepositRefundedAt.Departure,
    charge_per_move_amount: 0,

    currency: supplier?.currency ?? Currency.Aud,

    measurement: supplier?.measurement ?? Measurement.Metric,
    fixed_latest_departure_date: null!,
    extra_hire_unit_supplier_net_rate: null!,

    is_ferry_required: false,

    inclusions: {
      upsert: [],
      delete: [],
    },

    acknowledgements: {
      upsert: [],
      delete: [],
    },

    lines: {
      upsert: [],
      delete: [],
    },
  };
}
